import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import LevelBadge from "../../global/LevelBadge";
import { useAuth } from "../../global/AuthContext";
import { useNavigationReferrer } from "../../global/NavigationReferrer";
import {
  Detail,
  Market,
  Mine,
  Ranking,
  Summary,
  formatCredit as fc,
  formatTime,
  isClosed,
  marketApi,
  marketError,
  thumbnailUploadError,
} from "./api";
import "../../assets/css/container/market/MarketPages.css";
import { useMarketMetadata } from "./useMarketMetadata";

function MarketShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <main className="MarketPages">
      <header className="market-header">
        <div>
          <span className="market-eyebrow">FACTWEB PREDICTION</span>
          <h1>{title}</h1>
        </div>
        <nav aria-label="예측시장 메뉴">
          <Link to="/markets">예측시장</Link>
          <Link to="/markets/me">나의 예측</Link>
          <Link to="/portfolio/manage">나의예측공유</Link>
          <Link to="/markets/ranking">랭킹</Link>
        </nav>
      </header>
      {children}
    </main>
  );
}
export function CreditSummary({ summary }: { summary: Summary }) {
  return (
    <div className="market-summary">
      <div>
        Credit 총량
        <strong>
          {fc(Number(summary.availableCredit) + Number(summary.reservedCredit))}
        </strong>
        <small>
          사용 가능 {fc(summary.availableCredit)} · 주문 예약{" "}
          {fc(summary.reservedCredit)}
          <br />
          보유 지분 가치는 포함하지 않습니다.
        </small>
      </div>
      <div>
        성공 예측 정산금<strong>{fc(summary.predictionPayout)}</strong>
        <small>승리 지분의 누적 지급액 · 매수 원금 포함</small>
      </div>
      <div>
        정산된 시장 순손익<strong>{fc(summary.predictionProfit)}</strong>
        <small>매도금 + 결과 정산금 − 매수금</small>
      </div>
    </div>
  );
}
function Cards({ markets }: { markets: Market[] }) {
  return (
    <div className="market-grid">
      {markets.map((m) => (
        <Link className="market-card" to={`/markets/${m.id}`} key={m.id}>
          {m.thumbnail ? (
            <img
              src={m.thumbnail}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="market-placeholder">YES / NO</div>
          )}
          <div className="market-card-body">
            <small>
              {m.status === "SETTLED"
                ? `정산 완료 · ${m.result}`
                : isClosed(m)
                  ? "거래 마감"
                  : "거래 중"}
            </small>
            <h2>{m.title}</h2>
            <div className="market-ratio">
              <span>YES {m.yes_price}%</span>
              <span>NO {100 - m.yes_price}%</span>
            </div>
            <div className="market-bar">
              <i style={{ width: `${m.yes_price}%` }} />
            </div>
            <small>마감 {formatTime(m.closes_at)} (한국 시간) · 조회 {fc(m.view_count || 0)}</small>
          </div>
        </Link>
      ))}
    </div>
  );
}
export function MarketList() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [input, setInput] = useState(q);
  const [markets, setMarkets] = useState<Market[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setMarkets(null);
    setError("");
    marketApi
      .list(q, page)
      .then((r) => {
        if (active) setMarkets(r.data);
      })
      .catch((e) => {
        if (active) setError(marketError(e));
      });
    return () => {
      active = false;
    };
  }, [q, page]);
  return (
    <MarketShell title="예측시장">
      <p>
        폴리마켓처럼 YES·NO로 미래를 예측하고, FactWeb의 Credit으로 지분을 매수·매도하세요. 승리 지분 1주당 100 Credit으로
        정산됩니다.
      </p>
      <form
        className="market-search"
        onSubmit={(e) => {
          e.preventDefault();
          setParams({ q: input, page: "1" });
        }}
      >
        <input
          aria-label="예측주제 검색"
          placeholder="예측주제 검색"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <button>검색</button>
      </form>
      {error ? (
        <p role="alert">{error}</p>
      ) : markets ? (
        markets.length ? (
          <Cards markets={markets} />
        ) : (
          <p>등록된 예측주제가 없습니다.</p>
        )
      ) : (
        <p role="status">불러오는 중...</p>
      )}
      <div className="market-actions">
        <button
          disabled={page === 1}
          onClick={() => setParams({ q, page: String(page - 1) })}
        >
          이전
        </button>
        <span>{page} 페이지</span>
        <button
          disabled={!markets || markets.length < 24}
          onClick={() => setParams({ q, page: String(page + 1) })}
        >
          다음
        </button>
      </div>
    </MarketShell>
  );
}
export function MarketDetail() {
  const { id = "" } = useParams();
  const location = useLocation();
  const referrer = useNavigationReferrer();
  const [viewCount, setViewCount] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    setViewCount(null);
    marketApi.recordView(id, referrer, location.search).then(r => {
      if (active) setViewCount(r.data.viewCount);
    }).catch(() => {});
    return () => { active = false; };
  }, [id, location.key, location.search, referrer]);
  const { user } = useAuth();
  const signedIn = !!user && user.id > 0;
  const navigate = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [mine, setMine] = useState<Mine | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState("YES");
  const [side, setSide] = useState("BUY");
  const [price, setPrice] = useState(50);
  const [quantity, setQuantity] = useState(1);
  const load = useCallback(async () => {
    const r = await marketApi.detail(id);
    setData(r.data);
    if (signedIn) setMine((await marketApi.mine()).data);
  }, [id, signedIn]);
  useEffect(() => {
    let active = true;
    setData(null);
    setMine(null);
    setError("");
    marketApi
      .detail(id)
      .then((r) => {
        if (active) setData(r.data);
      })
      .catch((e) => {
        if (active) setError(marketError(e));
      });
    if (signedIn)
      marketApi
        .mine()
        .then((r) => {
          if (active) setMine(r.data);
        })
        .catch((e) => {
          if (active) setError(marketError(e));
        });
    const timer = window.setInterval(() => {
      marketApi.detail(id).then(r => { if (active) setData(r.data); }).catch(() => {});
      if (signedIn) marketApi.mine().then(r => { if (active) setMine(r.data); }).catch(() => {});
    }, 15000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [id, signedIn]);
  useMarketMetadata(data?.market);
  const action = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(marketError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!data)
    return (
      <MarketShell title="예측주제">
        <p role={error ? "alert" : "status"}>{error || "불러오는 중..."}</p>
      </MarketShell>
    );
  const m = data.market;
  const interested = !!mine?.interests.some((i) => i.market_id === m.id);
  const closed = isClosed(m);
  const orders = mine?.orders.filter((o) => o.market_id === m.id) || [];
  const positions = mine?.positions.filter((p) => p.market_id === m.id) || [];
  const history = data.trades
    .filter((t) => t.side === "BUY")
    .slice()
    .reverse();
  return (
    <MarketShell title={m.title}>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <div className="market-actions">
        <span>
          {closed ? "거래 마감" : "거래 중"} · {formatTime(m.closes_at)} 마감 · 조회 {fc(viewCount ?? m.view_count ?? 0)}
        </span>
        <button
          disabled={busy}
          onClick={() =>
            signedIn
              ? action(
                  () => marketApi.interest(id, !interested),
                  interested ? "관심 해제했습니다." : "관심 등록했습니다.",
                )
              : navigate("/login")
          }
        >
          {interested ? "★ 관심 해제" : "☆ 관심 등록"}
        </button>
      </div>
      <div className="market-detail-grid">
        <section className="market-panel">
          {m.thumbnail && (
            <img className="market-cover" src={m.thumbnail} alt="" />
          )}
          <div className="market-ratio">
            <strong>YES {m.yes_price}%</strong>
            <strong>NO {100 - m.yes_price}%</strong>
          </div>
          <small>
            최근 체결가 기준 · 첫 체결 전에는 기준값 50% · 확률을 보장하지
            않습니다.
          </small>
          <h2>가격 흐름</h2>
          {history.length ? (
            <svg
              className="market-chart"
              viewBox="0 0 600 150"
              role="img"
              aria-label="최근 체결 YES 환산 가격 차트"
            >
              <line x1="0" y1="75" x2="600" y2="75" stroke="#cbd5e1" />
              <polyline
                fill="none"
                stroke="#0284c7"
                strokeWidth="3"
                points={history
                  .map(
                    (t, i) =>
                      `${history.length === 1 ? 300 : (i * 600) / (history.length - 1)},${150 - (t.outcome === "YES" ? t.price : 100 - t.price) * 1.5}`,
                  )
                  .join(" ")}
              />
            </svg>
          ) : (
            <p>첫 거래를 기다리고 있습니다.</p>
          )}
          <h2>주제 설명</h2>
          <p className="market-text">{m.description}</p>
          <h2>판정 기준</h2>
          <p className="market-text">{m.rules}</p>
          {m.source_url && <a href={m.source_url} target="_blank" rel="noopener noreferrer">
            공식 판정 자료 보기
          </a>}
          {m.result && (
            <div className="market-resolution">
              <h2>결과: {m.result}</h2>
              <p className="market-text">{m.resolution}</p>
            </div>
          )}
          <h2>호가</h2>
          <div className="market-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>선택</th>
                  <th>주문</th>
                  <th>가격</th>
                  <th>수량</th>
                </tr>
              </thead>
              <tbody>
                {data.book.map((b, i) => (
                  <tr key={i}>
                    <td>{b.outcome}</td>
                    <td>{b.side === "BUY" ? "매수" : "매도"}</td>
                    <td>{b.price}</td>
                    <td>{fc(b.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.book.length && <p>대기 주문이 없습니다.</p>}
        </section>
        <aside className={`market-panel market-order market-order-${outcome.toLowerCase()}`}>
          <div className="market-order-heading">
            <h2>주문하기</h2><span className="market-order-type">지정가</span>
          </div>
          <div className="market-side-tabs" role="group" aria-label="주문 방향">
            <button type="button" aria-pressed={side === "BUY"} onClick={() => setSide("BUY")}>매수</button>
            <button type="button" aria-pressed={side === "SELL"} onClick={() => setSide("SELL")}>매도</button>
          </div>
          <div className="market-outcome-options" role="group" aria-label="예측 선택">
            {["YES", "NO"].map((value) => (
              <button key={value} type="button" className={`market-outcome-${value.toLowerCase()}`}
                aria-pressed={outcome === value} onClick={() => setOutcome(value)}>
                <strong>{value}</strong><span>{value === "YES" ? m.yes_price : 100 - m.yes_price}%</span>
              </button>
            ))}
          </div>
          <p className="market-order-caption">최근 체결가 기준 · 주문 가격은 아래에서 지정하세요.</p>
          {!signedIn && <p>매수·매도는 실제 계정으로 로그인한 뒤 이용할 수 있습니다.</p>}
          {mine && (
            <p>
              사용 가능 <b>{fc(mine.summary.availableCredit)} Credit</b>
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!signedIn) {
                navigate("/login");
                return;
              }
              action(
                () => marketApi.order(id, { outcome, side, price, quantity }),
                "주문을 접수했습니다. 미체결 수량은 대기 주문에 남습니다.",
              );
            }}
          >
            <label>
              가격 (Credit / 주)
              <input
                type="number"
                min="1"
                max="99"
                step="1"
                required
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
              />
            </label>
            <label>
              수량 (주)
              <input
                type="number"
                min="1"
                max="10000"
                step="1"
                required
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
            </label>
            <dl className="market-order-summary" aria-live="polite">
              <div><dt>{side === "BUY" ? "주문 총액" : "예상 매도금"}</dt><dd>{fc(price * quantity)} Credit</dd></div>
              {side === "BUY" ? <>
                <div><dt>적중 시 정산금</dt><dd>{fc(quantity * 100)} Credit</dd></div>
                <div className="market-order-profit"><dt>적중 시 순이익</dt><dd>{fc((100 - price) * quantity)} Credit</dd></div>
              </> : <div><dt>매도 가능</dt><dd>{fc(positions.filter(p => p.outcome === outcome).reduce((total, p) => total + p.quantity - p.reserved, 0))}주</dd></div>}
            </dl>
            <p className="market-order-caption">{side === "BUY"
              ? "입력한 지정가로 전량 체결 시 기준입니다. 예측이 틀리면 해당 지분의 정산금은 0 Credit입니다."
              : "입력한 지정가로 전량 체결 시 기준입니다. 매도한 지분은 결과 정산에서 제외됩니다."}</p>
            <button className="market-order-submit" disabled={busy || closed}>
              {!signedIn
                ? "로그인 후 거래"
                : closed
                  ? "마감된 시장"
                  : busy
                    ? "처리 중..."
                    : `${outcome} ${side === "BUY" ? "매수" : "매도"}`}
            </button>
          </form>
          <p>
            <small>
              상대 주문이 있어야 체결됩니다. 미체결 매수금·매도 지분은 예약되며
              취소하면 반환됩니다. 자기 주문끼리는 체결되지 않습니다.
            </small>
          </p>
          <h3>내 보유 지분</h3>
          {positions.map((p) => (
            <p key={p.outcome}>
              {p.outcome} {fc(p.quantity)}주 · 매도 예약 {fc(p.reserved)}주
            </p>
          ))}
          {!positions.length && <p>보유 지분이 없습니다.</p>}
          <h3>내 대기 주문</h3>
          {orders.map((o) => (
            <div className="market-open-order" key={o.id}>
              <span>
                {o.outcome} {o.side === "BUY" ? "매수" : "매도"} {o.price}{" "}
                Credit × {o.remaining}주
              </span>
              <button
                disabled={busy}
                onClick={() =>
                  action(
                    () => marketApi.cancel(m.id, o.id),
                    "주문을 취소했습니다.",
                  )
                }
              >
                취소
              </button>
            </div>
          ))}
        </aside>
      </div>
    </MarketShell>
  );
}
export function MyPredictions() {
  const [mine, setMine] = useState<Mine | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = () => marketApi.mine().then((r) => setMine(r.data));
  useEffect(() => {
    load().catch((e) => setError(marketError(e)));
  }, []);
  const cancel = async (market: number, id: number) => {
    setBusy(true);
    setError("");
    try {
      await marketApi.cancel(market, id);
      await load();
    } catch (e) {
      setError(marketError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MarketShell title="나의 예측">
      <div className="market-actions"><Link to="/portfolio/manage">나의예측공유</Link></div>
      {error && <p role="alert">{error}</p>}
      {mine ? (
        <>
          <CreditSummary summary={mine.summary} />
          <div className="market-actions">
            <button aria-pressed={tab === "all"} onClick={() => setTab("all")}>
              거래·관심 전체
            </button>
            <button
              aria-pressed={tab === "interest"}
              onClick={() => setTab("interest")}
            >
              관심등록
            </button>
          </div>
          <Cards
            markets={mine.markets.filter(
              (m) =>
                tab !== "interest" ||
                mine.interests.some((i) => i.market_id === m.id),
            )}
          />
          {!mine.markets.length && (
            <p>아직 거래하거나 관심 등록한 예측주제가 없습니다.</p>
          )}
          <h2>보유 지분</h2>
          <div className="market-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>주제</th>
                  <th>선택</th>
                  <th>보유 / 예약</th>
                  <th>매수 원가</th>
                </tr>
              </thead>
              <tbody>
                {mine.positions.map((p) => (
                  <tr key={`${p.market_id}-${p.outcome}`}>
                    <td>
                      <Link to={`/markets/${p.market_id}`}>{p.title}</Link>
                    </td>
                    <td>{p.outcome}</td>
                    <td>
                      {p.quantity} / {p.reserved}
                    </td>
                    <td>{fc(p.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h2>대기 주문</h2>
          {mine.orders.map((o) => (
            <div className="market-open-order" key={o.id}>
              <Link to={`/markets/${o.market_id}`}>{o.title}</Link>
              <span>
                {o.outcome} {o.side === "BUY" ? "매수" : "매도"} · {o.price} ×{" "}
                {o.remaining}
              </span>
              <button disabled={busy} onClick={() => cancel(o.market_id, o.id)}>
                취소
              </button>
            </div>
          ))}
          <h2>최근 거래 내역 (100건)</h2>
          <div className="market-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>주제</th>
                  <th>선택</th>
                  <th>종류</th>
                  <th>가격 × 수량</th>
                  <th>시간</th>
                </tr>
              </thead>
              <tbody>
                {mine.trades.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <Link to={`/markets/${t.market_id}`}>{t.title}</Link>
                    </td>
                    <td>{t.outcome}</td>
                    <td>{t.side === "BUY" ? "매수" : "매도"}</td>
                    <td>
                      {t.price} × {t.quantity}
                    </td>
                    <td>{formatTime(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        !error && <p>불러오는 중...</p>
      )}
    </MarketShell>
  );
}
export function MarketRanking() {
  const [sort, setSort] = useState("profit");
  const [period, setPeriod] = useState("all");
  const [rows, setRows] = useState<Ranking[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setError("");
    setRows(null);
    marketApi
      .ranking(sort, period)
      .then((r) => {
        if (active) setRows(r.data);
      })
      .catch((e) => {
        if (active) setError(marketError(e));
      });
    return () => {
      active = false;
    };
  }, [sort, period]);
  return (
    <MarketShell title="예측시장 랭킹">
      <p>정산된 시장만 집계합니다. 활동 보상과 무효 시장은 제외됩니다.</p>
      <div className="market-actions" role="group" aria-label="랭킹 기간">
        {[["all", "전체"], ["week", "주별"], ["month", "월별"], ["year", "년별"]].map(([value, label]) => (
          <button key={value} aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>
        ))}
      </div>
      <p><small>한국 시간 기준 · 주별은 이번 주 월요일부터, 월별은 이번 달 1일부터, 년별은 올해 1월 1일부터의 정산 결과입니다. 정산 시각이 없는 이전 기록은 전체에만 포함됩니다.</small></p>
      <label>
        순위 기준
        <select value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="profit">순손익</option>
          <option value="payout">성공 예측 정산금</option>
          <option value="wins">성공 시장 수</option>
          <option value="surprise">낮은 가격에서 성공</option>
        </select>
      </label>
      <p>
        <small>
          성공 시장: 정산 시 승리 지분을 보유한 시장. 낮은 가격 성공 점수: 승리
          지분의 수량 가중 평균 (100 − 매수가). 양쪽 지분 보유도 성공 시장에
          포함되므로 순손익을 함께 확인하세요.
        </small>
      </p>
      {error && <p role="alert">{error}</p>}
      {rows ? (
        <>
          <div className="market-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>순위</th>
                  <th>닉네임</th>
                  <th>순손익</th>
                  <th>성공 정산금</th>
                  <th>성공 / 정산 시장</th>
                  <th>낮은 가격 성공 점수</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.userId}>
                    <td>{i + 1}</td>
                    <td><span className="market-ranking-user"><LevelBadge level={r.level ?? 1} /><span>{r.nickname}</span></span></td>
                    <td>{fc(r.profit)}</td>
                    <td>{fc(r.payout)}</td>
                    <td>
                      {r.wins} / {r.settled}
                    </td>
                    <td>{r.surprise ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <p>정산된 예측 기록이 아직 없습니다.</p>}
        </>
      ) : (
        !error && <p>불러오는 중...</p>
      )}
    </MarketShell>
  );
}
function localDate(value: string | number) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
}
export function AdminMarkets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [selected, setSelected] = useState("");
  const [result, setResult] = useState("YES");
  const [resolution, setResolution] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Market | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    thumbnail: "",
    rules: "",
    sourceUrl: "",
    closesAt: "",
  });
  useEffect(() => {
    if (user?.role?.toLowerCase() === "admin")
      marketApi
        .list("", page)
        .then((r) => setMarkets(r.data))
        .catch((e) => setError(marketError(e)));
  }, [user, page]);
  if (user?.role?.toLowerCase() !== "admin")
    return (
      <MarketShell title="관리자">
        <p role="alert">관리자만 접근할 수 있습니다.</p>
      </MarketShell>
    );
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (uploading) return;
    setBusy(true);
    setError("");
    try {
      const body = {
        ...form,
        closesAt: editing && form.closesAt === localDate(editing.closes_at)
          ? new Date(editing.closes_at).toISOString() : new Date(form.closesAt).toISOString(),
      };
      if (editing) {
        await marketApi.update(editing.id, body);
        navigate(`/markets/${editing.id}`);
      } else {
        const r = await marketApi.create(body);
        navigate(`/markets/${r.data.id}`);
      }
    } catch (err) {
      setError(marketError(err));
    } finally {
      setBusy(false);
    }
  };
  const settle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !window.confirm(
        "결과를 정산하면 되돌릴 수 없습니다. 판정과 근거를 확인했습니까?",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await marketApi.resolve(Number(selected), { result, resolution });
      navigate(`/markets/${selected}`);
    } catch (err) {
      setError(marketError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MarketShell title="관리자 페이지">
      <nav className="market-actions">
        <a href="#market-create">예측주제 작성</a>
        <a href="#market-settle">결과 판정·정산</a>
      </nav>
      {error && <p role="alert">{error}</p>}
      <section id="market-create" className="market-panel">
        <h2>{editing ? `예측주제 수정 #${editing.id}` : "예측주제 작성"}</h2>
        <label>수정할 예측주제
          <select disabled={busy || uploading} value={editing?.id || ""} onChange={(e) => {
            const m = markets.find(item => item.id === Number(e.target.value));
            setEditing(m || null);
            setError("");
            setForm(m ? { title: m.title, description: m.description, thumbnail: m.thumbnail || "", rules: m.rules, sourceUrl: m.source_url, closesAt: localDate(m.closes_at) }
              : { title: "", description: "", thumbnail: "", rules: "", sourceUrl: "", closesAt: "" });
          }}>
            <option value="">새 주제 작성</option>
            {markets.map(m => <option key={m.id} value={m.id}>#{m.id} {m.title}</option>)}
          </select>
        </label>
        <div className="market-actions">
          <button type="button" disabled={busy || page === 1} onClick={() => setPage(page - 1)}>이전 주제</button>
          <span>{page} 페이지</span>
          <button type="button" disabled={busy || markets.length < 24} onClick={() => setPage(page + 1)}>다음 주제</button>
        </div>
        <form onSubmit={create}>
          <label>썸네일 이미지 업로드 (선택)
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy || uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024 || file.size === 0) {
                  setError("JPG·PNG·WebP 이미지를 10MB 이하로 선택하세요."); return;
                }
                setUploading(true); setError("");
                try {
                  const response = await marketApi.uploadThumbnail(file);
                  if (!response.data.url) throw new Error("Upload failed");
                  setForm(current => ({ ...current, thumbnail: response.data.url }));
                } catch (err) { setError(thumbnailUploadError(err)); }
                finally { setUploading(false); }
              }} />
          </label>
          <p className="market-order-caption">JPG·PNG·WebP, 최대 10MB. 이미지를 올린 뒤 주제를 저장하세요.</p>
          {uploading && <p role="status">썸네일 업로드 중...</p>}
          {form.thumbnail && <div className="market-thumbnail-preview">
            <img src={form.thumbnail} alt="예측주제 썸네일 미리보기" />
            <button type="button" disabled={busy || uploading} onClick={() => setForm(current => ({ ...current, thumbnail: "" }))}>썸네일 제거</button>
          </div>}
          <label>썸네일 URL (선택)
            <input type="text" maxLength={1000} disabled={busy || uploading} value={form.thumbnail}
              onChange={e => setForm(current => ({ ...current, thumbnail: e.target.value }))} />
          </label>
          {(
            [
              ["title", "주제명"],
              ["sourceUrl", "공식 판정 자료 URL (선택)"],
              ["closesAt", "거래 마감 (현재 기기 시간대)"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              {label}
              <input
                type={
                  key === "closesAt"
                    ? "datetime-local"
                    : key === "title"
                      ? "text"
                      : "url"
                }
                required={key !== "sourceUrl"}
                step={key === "closesAt" ? "1" : undefined}
                maxLength={key === "title" ? 200 : 1000}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </label>
          ))}
          {(
            [
              ["description", "주제 설명"],
              ["rules", "YES / NO 판정 기준과 발표 지연 시 처리"],
            ] as const
          ).map(([key, label]) => (
            <label key={key}>
              {label}
              <textarea
                required
                maxLength={key === "description" ? 20000 : 10000}
                rows={6}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </label>
          ))}
          <p>
            무효 판정 시 보유 지분 1주당 50 Credit을 반환합니다. 주문이 있거나 마감된
            시장은 판정 기준·자료 URL·마감 시간을 변경할 수 없습니다. 설명 수정 시 기존 판정의 의미를 유지하세요.
          </p>
          <button disabled={busy || uploading}>
            {uploading ? "이미지 업로드 중..." : busy ? "처리 중..." : editing ? "수정 저장" : "예측주제 등록"}
          </button>
        </form>
      </section>
      <section id="market-settle" className="market-panel">
        <h2>결과 판정·정산</h2>
        <form onSubmit={settle}>
          <label>
            정산할 시장
            <select
              required
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">시장 선택</option>
              {markets
                .filter((m) => m.status === "OPEN" && isClosed(m))
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    #{m.id} {m.title}
                  </option>
                ))}
            </select>
          </label>
          <div className="market-actions">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              이전 시장
            </button>
            <span>{page} 페이지</span>
            <button
              type="button"
              disabled={markets.length < 24}
              onClick={() => setPage(page + 1)}
            >
              다음 시장
            </button>
          </div>
          <label>
            결과
            <select value={result} onChange={(e) => setResult(e.target.value)}>
              <option>YES</option>
              <option>NO</option>
              <option value="VOID">무효 (지분당 50 Credit 반환)</option>
            </select>
          </label>
          <label>
            판정 근거와 자료 URL
            <textarea
              required
              maxLength={10000}
              rows={5}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            />
          </label>
          <button disabled={busy || !selected}>판정 확정 및 정산</button>
        </form>
      </section>
    </MarketShell>
  );
}
